import {
  Alert,
  Button,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
} from 'lucide-react'
import {
  useEffect,
  useState,
} from 'react'
import {
  Link,
  useNavigate,
  useParams,
} from 'react-router-dom'

import { validatePhoneNumberStructure } from '@common/utils/helperFunctions'
import { decodeInviteTokenSafely } from '@common/utils/validation/authValidators'
import { useAuthStore } from '@store/auth.store'
import { OptionsMap } from '../DeviceManagement/DeviceInformationManager/constants'
import { AuthShell } from './AuthShell'
import {
  getAuthPasswordValidationError,
  getConfirmPasswordError,
  PasswordRulesMessage,
} from './authFormHelpers'

const REDIRECT_DELAY_SECONDS = 20

interface ConfirmEmailFormValues {
  firstName: string
  lastName: string
  mobile: string
  password: string
  confirmPassword: string
}

const getRequiredError = (value: string) => {
  return value.trim() ? null : 'Required'
}

const getMobileError = (mobile: string) => {
  if (!mobile) {
    return 'Mobile is required'
  }

  return validatePhoneNumberStructure(mobile) ? null : 'Please enter a valid mobile number'
}

const ConfirmEmail = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [secondsRemaining, setSecondsRemaining] = useState(REDIRECT_DELAY_SECONDS)
  const registerUser = useAuthStore((state) => state.registerUser)
  const registeringUser = useAuthStore((state) => state.registeringUser)
  const userRegistered = useAuthStore((state) => state.userRegistered)
  const registerUserError = useAuthStore((state) => state.registerUserError)
  const resetAuthFormState = useAuthStore((state) => state.resetAuthFormState)

  const decodedInviteToken = decodeInviteTokenSafely(token)
  const defaultUom = decodedInviteToken?.country
    ? OptionsMap[decodedInviteToken.country.trim()]?.uom ?? 'Liters'
    : 'Liters'

  const form = useForm<ConfirmEmailFormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      mobile: '',
      password: '',
      confirmPassword: '',
    },
    validateInputOnBlur: true,
    transformValues: (values) => ({
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      mobile: values.mobile.trim(),
      password: values.password,
      confirmPassword: values.confirmPassword,
    }),
    validate: {
      firstName: getRequiredError,
      lastName: getRequiredError,
      mobile: getMobileError,
      password: getAuthPasswordValidationError,
      confirmPassword: (value, values) =>
        getConfirmPasswordError(values.password, value),
    },
  })

  useEffect(() => {
    resetAuthFormState()
  }, [resetAuthFormState])

  // A single interval drives both the countdown and the redirect below. A separate timeout for
  // the redirect could disagree with the number on screen by up to a second.
  useEffect(() => {
    if (!userRegistered) {
      return
    }

    const interval = setInterval(() => {
      setSecondsRemaining((current) => (current > 0 ? current - 1 : 0))
    }, 1000)

    return () => clearInterval(interval)
  }, [userRegistered])

  useEffect(() => {
    if (userRegistered && secondsRemaining === 0) {
      navigate('/', { replace: true })
    }
  }, [
    navigate,
    secondsRemaining,
    userRegistered,
  ])

  const handleSubmit = async (values: ConfirmEmailFormValues) => {
    if (!token) {
      return
    }

    await registerUser({
      firstname: values.firstName,
      lastname: values.lastName,
      mobile: values.mobile,
      newPassword: values.password,
      newPasswordConfirmation: values.confirmPassword,
      token,
      uom: defaultUom,
    })
  }

  if (!token) {
    return (
      <AuthShell title={'Create your account'}>
        <Alert
          color={'red'}
          icon={<AlertCircle size={16} />}
        >
          Invalid token
        </Alert>
      </AuthShell>
    )
  }

  if (userRegistered) {
    return (
      <AuthShell title={'Create your account'}>
        <Stack gap={'md'}>
          <Alert
            color={'teal'}
            icon={<CheckCircle size={16} />}
            title={'Account created'}
          >
            Your account is ready. Sign in with your email address and new password.
          </Alert>
          <Text
            size={'sm'}
            c={'gray.6'}
          >
            {`Redirecting you to the login page in ${secondsRemaining} ${secondsRemaining === 1 ? 'second' : 'seconds'}...`}
          </Text>
          <Button
            component={Link}
            to={'/'}
          >
            Login
          </Button>
        </Stack>
      </AuthShell>
    )
  }

  return (
    <AuthShell title={'Create your account'}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={'md'}>
          <TextInput
            label={'First name'}
            placeholder={'Enter a Firstname'}
            required
            {...form.getInputProps('firstName')}
          />
          <TextInput
            label={'Last name'}
            placeholder={'Enter a Lastname'}
            required
            {...form.getInputProps('lastName')}
          />
          <TextInput
            label={'Mobile'}
            placeholder={'Enter a Mobile'}
            type={'tel'}
            required
            {...form.getInputProps('mobile')}
          />
          <PasswordInput
            label={'New Password'}
            placeholder={'Enter new password'}
            autoComplete={'new-password'}
            required
            {...form.getInputProps('password')}
          />
          <PasswordRulesMessage password={form.values.password} />
          <PasswordInput
            label={'Confirm New Password'}
            placeholder={'Enter new password again'}
            autoComplete={'new-password'}
            required
            {...form.getInputProps('confirmPassword')}
          />
          <Button
            type={'submit'}
            loading={registeringUser}
            disabled={
              !form.values.firstName ||
              !form.values.lastName ||
              !form.values.mobile ||
              !form.values.password ||
              !form.values.confirmPassword
            }
          >
            Confirm
          </Button>
          {registerUserError && (
            <Alert
              color={'red'}
              icon={<AlertCircle size={16} />}
            >
              An Error occurred while registering your account. Please try again...
            </Alert>
          )}
          <Button
            component={Link}
            to={'/'}
            variant={'default'}
            leftSection={<ArrowLeft size={16} />}
          >
            Back to Login
          </Button>
        </Stack>
      </form>
    </AuthShell>
  )
}

export default ConfirmEmail
